import { Modal, PaymentProcessorEnum, PrimaryButton, SecondaryButton, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { InternalError } from '@systemeio/util'
import {
  refundTransaction,
  refundTransactionNew,
} from 'modules/payment/transactions/api/transactions-api'
import React, { useState } from 'react'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface RefundConfirmationProps {
  opened: boolean
  transactionId: number
  paymentProcessorName: PaymentProcessorEnum
  onClose: () => void
  onUpdate: () => Promise<void>
}

const RefundConfirmation = ({
  opened,
  transactionId,
  paymentProcessorName,
  onClose,
  onUpdate,
}: RefundConfirmationProps) => {
  const { t } = useLocoTranslation()
  const [error, setError] = useState('')
  const [isFetching, setIsFetching] = useState(false)

  const handleRefund = async () => {
    if (paymentProcessorName === 'secure-and-pay') {
      await refundTransactionNew(transactionId, null, paymentProcessorName)
    } else {
      await refundTransaction(transactionId)
    }
  }

  const onRefund = async () => {
    try {
      setIsFetching(true)
      await handleRefund()
      setIsFetching(false)
      await onUpdate()
      toast.success(t('dashboard.transactions.payment_has_refunded'))
      onClose()
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''))
        }
      } else if (e instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      }
    }
  }

  const onModalClose = () => setError('')

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={t('dashboard.transactions.refund_confirmation')}
        afterLeave={onModalClose}
        isFetching={isFetching}
        isCentered={true}
      >
        <form className="flex flex-col gap-6">
          <div className="flex justify-center">
            <p>{t('dashboard.transactions.refund_confirmation-text')}</p>
          </div>
          <div className="flex justify-center gap-4">
            <SecondaryButton
              width="large"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                onClose()
              }}
              disabled={isFetching}
            >
              {t('confirmation_modal.cancel')}
            </SecondaryButton>
            <PrimaryButton
              isFetching={isFetching}
              width="large"
              onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                await onRefund()
              }}
              type="submit"
            >
              {t('confirmation_modal.confirm')}
            </PrimaryButton>
          </div>
          <FieldErrorAndDescription error={error} errorClassName={'text-center mt-2'} />
        </form>
      </Modal>
    </>
  )
}

export default RefundConfirmation
